import { client } from "@/hono";
import { useWikiSpacePages } from "@/hooks/use-wiki-space-pages";
import { EditWikiSpaceModal } from "@/modals/wiki/edit-wiki-space-modal";
import { Button } from "@msuite/ui/button";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon, icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { Menu } from "@msuite/ui/menu";
import { ConfirmModal, useAsyncModal } from "@msuite/ui/modal";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { StatusIndicator } from "@msuite/ui/status-indicator";
import { cn, responseHandler } from "@msuite/ui/utils";
import type { Nullable, Tables } from "@msuite/utils/types";
import React from "react";
import { useNavigate } from "react-router";
import { WikiPageTree } from "./wiki-page-tree";

/** Props Interface */
interface WikiSpaceItemProps {
	space: Tables<"wikiSpaces">;
	isActive: boolean;
	activePageId: Nullable<string>;
}

export function WikiSpaceItem({ space, isActive, activePageId }: WikiSpaceItemProps) {
	/** Hooks */
	const { t } = useTranslation();
	const navigate = useNavigate();
	const qc = useQueryClient();
	const editSpaceModal = useDisclosure();
	const deleteSpaceModal = useAsyncModal();

	/** Query */
	const { wikiSpacePages } = useWikiSpacePages(isActive ? space.id : null);

	/** Functions */
	async function handleOnAddPage() {
		const response = await client.wiki.spaces[":spaceId"].page.$post({
			param: { spaceId: space.id },
			json: { parentPageId: null },
		});
		const data = await responseHandler(response);
		await qc.invalidateQueries({ predicate: invalidate(["wiki-spaces", space.id, "pages"]) });
		if (data.page) navigate(`/wiki/${space.id}/${data.page.id}?create=true`);
	}

	async function handleOnDeleteSpace() {
		const isConfirmed = await deleteSpaceModal.confirm();
		if (!isConfirmed) return;
		const response = await client.wiki.spaces[":spaceId"].$delete({
			param: { spaceId: space.id },
		});
		await responseHandler(response);
		await qc.invalidateQueries({ predicate: invalidate(["wiki-spaces"]) });
		if (isActive) navigate("/wiki");
	}

	/** Render */
	return (
		<React.Fragment>
			{/* The row and its pages share the gap the tree uses between its own items, so the first
			    page's connector meets the row's bottom edge exactly. */}
			<div className="flex flex-col gap-0.5">
				<div
					className={cn("group h-stack justify-between cursor-pointer", "card")}
					onClick={() => navigate(`/wiki/${space.id}`)}
					onKeyUp={() => navigate(`/wiki/${space.id}`)}
				>
					<div className="h-stack min-w-0">
						<StatusIndicator color={space.color} />
						<p className={cn("truncate", isActive && "font-medium")}>{space.name}</p>
					</div>
					<div
						className="opacity-0 group-hover:opacity-100"
						onClick={(event) => event.stopPropagation()}
						onKeyUp={(event) => event.stopPropagation()}
					>
						<Menu
							items={() => [
								{
									icon: icon("Plus"),
									label: t("wiki.add_page"),
									onClick: handleOnAddPage,
								},
								{
									icon: icon("Edit"),
									label: t("wiki.edit_space"),
									onClick: editSpaceModal.onOpen,
								},
								"divider",
								{
									icon: icon("Trash2"),
									label: t("wiki.delete_space"),
									onClick: handleOnDeleteSpace,
									isDanger: true,
								},
							]}
						>
							<Button
								size="xxs"
								variant="ghost"
								icon={<Icon name="EllipsisVertical" />}
							/>
						</Menu>
					</div>
				</div>
				{isActive && (wikiSpacePages ?? []).length > 0 && (
					<WikiPageTree
						// The spine lands under the dot's centre: the card's half-pixel border, its
						// padding (px-3), then half of the dot (w-2).
						className="ml-[calc(1rem+0.5px)]"
						spaceId={space.id}
						pages={wikiSpacePages ?? []}
						activePageId={activePageId}
					/>
				)}
			</div>
			{editSpaceModal.isOpen && (
				<EditWikiSpaceModal
					isOpen={editSpaceModal.isOpen}
					onClose={editSpaceModal.onClose}
					spaceId={space.id}
				/>
			)}
			<ConfirmModal
				controller={deleteSpaceModal}
				title={t("wiki.delete_space")}
				message={t("wiki.are_you_sure_you_want_to_delete_this_space")}
			/>
		</React.Fragment>
	);
}
